import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, CalendarDays, Gift } from "lucide-react";
import EventGameForm from "../components/EventGameForm";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
  }),
};

const campaign = {
  slug: "ford-event-game-july-2026",
  name: "Ford Event Game July 2026",
  collectionName: "ford-event-game-july-2026",
  eventDate: "2026-07-04",
};

const prizes = [
  {
    image: "/images/event-game/capri.jpg",
    title: "Уикенд тест драйв",
    description: "с автомобил Ford по избор",
  },
  {
    image: "/images/event-game/t-shirt.jpeg",
    title: "Тениска Ford",
    description: "Оригинална тениска Ford.",
  },
  {
    image: "/images/event-game/cap.jpg",
    title: "Шапка Ford",
    description: "Оригинална шапка Ford.",
  },
];

function FordEventGame() {
  useEffect(() => {
    document.title = "Ford | Играй и спечели награда";
  }, []);

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#f7f9fc_0%,#ffffff_38%,#eef4ff_100%)] font-fordAntennaRegular text-slate-900">
      <section className="relative overflow-hidden isolate lg:overflow-visible">
        <div className="absolute inset-x-0 top-0 h-[560px] bg-[radial-gradient(circle_at_top_left,rgba(0,51,160,0.22),transparent_42%),radial-gradient(circle_at_85%_18%,rgba(82,167,255,0.18),transparent_28%),linear-gradient(135deg,#001a70_0%,#0036b3_52%,#0b4dff_100%)]" />
        <div className="absolute left-[8%] top-24 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute right-[10%] top-16 h-52 w-52 rounded-full bg-[#8cc8ff]/20 blur-3xl" />

        <div className="relative mx-auto max-w-[1280px] px-5 pb-24 pt-16 sm:px-8 lg:px-10 lg:pb-36 lg:pt-20">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={0}
            className="max-w-4xl text-white"
          >
            <h1 className="max-w-4xl font-fordBlack text-5xl uppercase leading-[0.92] sm:text-6xl lg:text-7xl">
              Регистрирай се и участвай за награда от Ford
            </h1>
            <p className="max-w-2xl mt-6 text-base leading-7 text-white/85 sm:text-lg">
              Включи се в томболата за оригинални награди Ford.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={0.1}
            className="grid gap-4 mt-10 sm:grid-cols-2 lg:absolute lg:bottom-[-120px] lg:left-10 lg:mt-0 lg:max-w-3xl lg:grid-cols-3"
          >
            <div className="rounded-[1.75rem] border border-[#0b4dff] bg-[linear-gradient(135deg,#001a70_0%,#0036b3_52%,#0b4dff_100%)] px-5 py-5 text-white shadow-[0_18px_40px_rgba(0,26,112,0.28)] sm:border-white/35 sm:bg-white/38 sm:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.18)] sm:backdrop-blur-md">
              <CalendarDays size={20} className="text-white" />
              <p className="mt-4 text-sm uppercase tracking-[0.18em] text-white">
                Събитие
              </p>
              <p className="mt-2 text-lg font-medium text-white">
                31 юли - 9 август 2026 г.
              </p>
            </div>
            <div className="rounded-[1.75rem] border border-[#0b4dff] bg-[linear-gradient(135deg,#001a70_0%,#0036b3_52%,#0b4dff_100%)] px-5 py-5 text-white shadow-[0_18px_40px_rgba(0,26,112,0.28)] sm:border-white/35 sm:bg-white/38 sm:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.18)] sm:backdrop-blur-md">
              <Gift size={20} className="text-white" />
              <p className="mt-4 text-sm uppercase tracking-[0.18em] text-white">
                Награди
              </p>
              <p className="mt-2 text-lg font-medium text-white">
                3 подаръка
                <br />
                от Ford
              </p>
            </div>
            <a
              href="#registration"
              className="group rounded-[1.75rem] border border-[#0b4dff] bg-[linear-gradient(135deg,#001a70_0%,#0036b3_52%,#0b4dff_100%)] px-5 py-5 text-white shadow-[0_18px_40px_rgba(0,26,112,0.28)] transition-colors hover:brightness-110 sm:border-white/35 sm:bg-white/38 sm:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.18)] sm:backdrop-blur-md sm:hover:bg-white/46 sm:hover:brightness-100"
            >
              <ArrowRight size={20} className="text-white" />
              <p className="mt-4 text-sm uppercase tracking-[0.18em] text-white">
                Участие
              </p>
              <p className="flex items-center gap-2 mt-2 text-lg font-medium text-white">
                Към формата
                <ArrowRight
                  size={16}
                  className="text-white transition-transform group-hover:translate-x-1"
                />
              </p>
            </a>
          </motion.div>
        </div>
      </section>

      <section className="relative z-10 pt-24 pb-20 -mt-10 lg:pt-44 lg:mt-0">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-10">
          <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-stretch">
            <div className="h-full">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.25 }}
                variants={fadeUp}
                custom={0}
                className="flex h-full flex-col rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_30px_90px_rgba(15,23,42,0.08)]"
              >
                {/* <p className="text-sm font-semibold uppercase tracking-[0.22em] text-fordBlue">
                  Награди
                </p> */}
                <h2 className="mt-3 text-3xl leading-none uppercase font-fordBlack text-slate-900">
                  Награди
                </h2>
                <div className="grid flex-1 gap-4 mt-8">
                  {prizes.map((prize) => (
                    <div
                      key={prize.title}
                      className="grid gap-4 rounded-[1.75rem] border border-slate-200 bg-slate-50/80 p-5 sm:grid-cols-[148px_1fr] sm:items-center"
                    >
                      <div className="overflow-hidden rounded-[1.4rem] bg-white shadow-[inset_0_0_0_1px_rgba(148,163,184,0.18)]">
                        <img
                          src={prize.image}
                          alt={prize.title}
                          className="h-[148px] w-full object-cover"
                        />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-slate-900">
                          {prize.title}
                        </h3>
                        <p className="mt-2 text-sm leading-6 text-slate-600">
                          {prize.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            <motion.div
              id="registration"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeUp}
              custom={0.12}
              className="h-full"
            >
              <EventGameForm campaign={campaign} />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default FordEventGame;
